import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import AudioSourceSelector, { type AudioSourceConfig } from '../components/AudioSourceSelector';
import AudioLevelMeter from '../components/AudioLevelMeter';
import AudioMixerControls from '../components/AudioMixerControls';
import { type WhipClient, type WhipConnectionState } from '../utils/whipClient';
import { defaultWhipClientFactory, type WhipClientFactory } from '../utils/whipClientFactory';
import { apiClient } from '../api/client';
import { type MixRatio } from '../utils/audioMixer';

interface HostDashboardProps {
  whipClientFactory?: WhipClientFactory;
}

export default function HostDashboard({ whipClientFactory = defaultWhipClientFactory }: HostDashboardProps = {}) {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, clearAuth } = useAuthStore();
  const [listenerCount, setListenerCount] = useState(0);
  const [isStreaming, setIsStreaming] = useState(false);
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  const [audioConfig, setAudioConfig] = useState<AudioSourceConfig | null>(null);
  const [error, setError] = useState('');
  const [connectionState, setConnectionState] = useState<string>('disconnected');
  const [isConnecting, setIsConnecting] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const whipClientRef = useRef<WhipClient | null>(null);
  const [sessionInfo, setSessionInfo] = useState({
    passcode: '',
    invitationLink: '',
    sessionName: '',
    duration: 0,
    startedAt: null as Date | null,
  });
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const pollingIntervalRef = useRef<number | null>(null);
  const durationIntervalRef = useRef<number | null>(null);

  const handleLogout = () => {
    clearAuth();
    navigate('/');
  };

  // Fetch session details from API
  const fetchSessionDetails = useCallback(async () => {
    if (!sessionId) return;

    // Check if we have session data from navigation state (from CreateSession)
    const navigationState = location.state as {
      passcode?: string;
      invitationLink?: string;
      sessionName?: string;
    } | null;

    try {
      const response = await apiClient.authenticatedRequest<{
        sessionId: string;
        sessionName: string;
        hostName: string;
        status: string;
        createdAt: string;
        startedAt: string | null;
        endedAt: string | null;
        maxListeners: number;
        listenerCount: number;
        duration: number;
        isHost: boolean;
      }>(`/api/sessions/${sessionId}`, { method: 'GET' });

      // Use navigation state for passcode/link if available, otherwise leave empty
      setSessionInfo({
        passcode: navigationState?.passcode || '',
        invitationLink: navigationState?.invitationLink || '',
        sessionName: response.sessionName,
        duration: 0,
        startedAt: response.startedAt ? new Date(response.startedAt) : null,
      });
      setListenerCount(response.listenerCount);
      setIsLoadingSession(false);
      setError(''); // Clear any previous errors
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('Failed to fetch session details:', errorMessage);
      setError('Failed to load session details. Please try again.');
      setIsLoadingSession(false);
    }
  }, [sessionId, location.state]);

  // Poll for listener count
  const pollListenerCount = useCallback(async () => {
    if (!sessionId || !isStreaming) return;

    try {
      const response = await apiClient.authenticatedRequest<{
        sessionId: string;
        sessionName: string;
        hostName: string;
        status: string;
        createdAt: string;
        startedAt: string | null;
        endedAt: string | null;
        maxListeners: number;
        listenerCount: number;
        duration: number;
        isHost: boolean;
      }>(`/api/sessions/${sessionId}`, { method: 'GET' });

      setListenerCount(response.listenerCount);
    } catch (err) {
      console.error('Failed to fetch listener count:', err);
      // Don't show error to user for polling failures
    }
  }, [sessionId, isStreaming]);

  // Start polling for listener count when streaming
  useEffect(() => {
    if (isStreaming) {
      // Poll immediately
      pollListenerCount();

      // Then poll every 5 seconds
      pollingIntervalRef.current = window.setInterval(() => {
        pollListenerCount();
      }, 5000);
    } else {
      // Stop polling when not streaming
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [isStreaming, sessionId, pollListenerCount]);

  // Update duration timer
  useEffect(() => {
    if (sessionInfo.startedAt) {
      // Calculate initial duration
      const now = new Date();
      const elapsed = Math.floor((now.getTime() - sessionInfo.startedAt.getTime()) / 1000);
      setSessionInfo(prev => ({ ...prev, duration: elapsed }));

      // Update duration every second
      durationIntervalRef.current = window.setInterval(() => {
        setSessionInfo(prev => ({
          ...prev,
          duration: prev.duration + 1,
        }));
      }, 1000);
    } else {
      // Clear timer if no start time
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }
    }

    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, [sessionInfo.startedAt]);

  // Load session details on mount
  useEffect(() => {
    fetchSessionDetails();
  }, [fetchSessionDetails]);

  const handleAudioSourceSelected = async (config: AudioSourceConfig, stream: MediaStream) => {
    setAudioConfig(config);
    setAudioStream(stream);
    setError('');
    setIsConnecting(true);

    try {
      // Get auth token
      const authToken = apiClient.getAuthToken();
      if (!authToken) {
        throw new Error('Not authenticated. Please log in again.');
      }

      // Create WHIP client using factory
      const whipClient = whipClientFactory({
        sessionId: sessionId!,
        apiUrl: config.apiUrl,
        authToken,
        enableAutoReconnect: true,
        maxReconnectAttempts: 3,
      });

      // Set up connection state monitoring
      whipClient.onStateChange((state: WhipConnectionState) => {
        setConnectionState(state.state);
        setIsReconnecting(state.reconnecting || false);
        console.log('Connection state:', state);

        // Handle connection state changes
        if (state.reconnecting) {
          setError(`Connection lost. Reconnecting... (Attempt ${state.reconnectAttempt || 0}/3)`);
        } else if (state.state === 'connected') {
          setIsStreaming(true);
          setIsConnecting(false);
          setError('');
        } else if (state.state === 'connecting') {
          setError('');
        }
      });

      // Set up error handling
      whipClient.onError((err: Error) => {
        console.error('WHIP client error:', err);

        // If max reconnection attempts reached, stop streaming but preserve client and error for manual retry
        if (err.message.includes('Maximum reconnection attempts') || err.message.includes('Failed to reconnect after multiple attempts')) {
          handleStopStreaming(true); // Preserve client for manual retry
          // Set error after stopping to preserve it
          setError(err.message);
        } else {
          setError(err.message);
        }
      });

      // Connect to backend
      await whipClient.connect(stream);

      // Store client reference
      whipClientRef.current = whipClient;

      console.log('WHIP connection established');
    } catch (err) {
      console.error('Failed to establish WHIP connection:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to connect to streaming server';
      setError(errorMessage);

      // Clean up resources on error
      if (config.mixer) {
        // If using mixer, let it handle cleanup of source streams
        config.mixer.cleanup();
      } else {
        // Otherwise, stop the stream tracks directly
        stream.getTracks().forEach(track => track.stop());
      }

      setAudioStream(null);
      setAudioConfig(null);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleAudioError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleStopStreaming = (preserveClient = false) => {
    // Disconnect WHIP client
    if (whipClientRef.current) {
      whipClientRef.current.disconnect();
      if (!preserveClient) {
        whipClientRef.current = null;
      }
    }

    // Clean up audio mixer if present
    if (audioConfig?.mixer) {
      audioConfig.mixer.cleanup();
    }

    // Stop audio tracks
    if (audioStream) {
      audioStream.getTracks().forEach(track => track.stop());
      setAudioStream(null);
    }

    setAudioConfig(null);
    setIsStreaming(false);
    setIsConnecting(false);
    setConnectionState('disconnected');
    if (!preserveClient) {
      setError('');
    }
  };

  const handleMixRatioChange = (ratio: MixRatio) => {
    console.log('Mix ratio changed:', ratio);
  };

  const handleEndSession = async () => {
    // Stop streaming first
    handleStopStreaming();

    if (!sessionId) return;

    try {
      await apiClient.authenticatedRequest(`/api/sessions/${sessionId}`, {
        method: 'DELETE',
      });
      console.log('Session ended successfully');
      navigate('/create-session');
    } catch (err) {
      console.error('Failed to end session:', err);
      setError('Failed to end session. Please try again.');
    }
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      handleStopStreaming();
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // TODO: Show toast notification
    console.log('Copied to clipboard:', text);
  };

  // Get connection quality indicator
  const getConnectionQuality = (): 'excellent' | 'good' | 'poor' | 'reconnecting' | 'disconnected' => {
    if (isReconnecting) {
      return 'reconnecting';
    }
    if (!isStreaming || connectionState === 'disconnected' || connectionState === 'failed') {
      return 'disconnected';
    }
    if (connectionState === 'connected') {
      return 'excellent';
    }
    if (connectionState === 'connecting') {
      return 'good';
    }
    return 'poor';
  };

  const getConnectionQualityDisplay = () => {
    const quality = getConnectionQuality();
    switch (quality) {
      case 'excellent':
        return '🟢 Excellent';
      case 'good':
        return '🟡 Good';
      case 'poor':
        return '🟠 Poor';
      case 'reconnecting':
        return '🟡 Reconnecting...';
      case 'disconnected':
        return '🔴 Disconnected';
    }
  };

  const handleManualReconnect = async () => {
    if (!whipClientRef.current) return;

    setError('');
    setIsConnecting(true);

    try {
      await whipClientRef.current.reconnect();
      setError('');
    } catch (err) {
      console.error('Manual reconnection failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to reconnect';
      setError(errorMessage);
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <div className="dashboard-container">
      <div className="header-with-logout">
        <h1>Host Dashboard</h1>
        <div className="user-info">
          <span>Welcome, {user?.displayName}</span>
          <button
            type="button"
            onClick={handleLogout}
            className="logout-button"
          >
            Logout
          </button>
        </div>
      </div>

      {isLoadingSession ? (
        <div className="loading-message">
          <p>Loading session details...</p>
        </div>
      ) : (
        <>
          <div className="session-info">
            <h2>Session Information</h2>
            {sessionInfo.sessionName && (
              <div className="info-item">
                <label>Session Name:</label>
                <span>{sessionInfo.sessionName}</span>
              </div>
            )}
            <div className="info-item">
              <label>Session ID:</label>
              <span>{sessionId}</span>
            </div>
            <div className="info-item">
              <label>Passcode:</label>
              <span>{sessionInfo.passcode || 'Loading...'}</span>
              {sessionInfo.passcode && (
                <button onClick={() => copyToClipboard(sessionInfo.passcode)}>
                  Copy
                </button>
              )}
            </div>
            <div className="info-item">
              <label>Invitation Link:</label>
              <span className="truncate">{sessionInfo.invitationLink || 'Loading...'}</span>
              {sessionInfo.invitationLink && (
                <button onClick={() => copyToClipboard(sessionInfo.invitationLink)}>
                  Copy
                </button>
              )}
            </div>
            <div className="info-item">
              <label>Duration:</label>
              <span>{formatDuration(sessionInfo.duration)}</span>
            </div>
            <div className="info-item">
              <label>Listeners:</label>
              <span>{listenerCount}</span>
            </div>
            {isStreaming && (
              <div className="info-item">
                <label>Connection Quality:</label>
                <span>{getConnectionQualityDisplay()}</span>
              </div>
            )}
          </div>

          <div className="streaming-controls">
            <h2>Streaming Controls</h2>

            {error && (
              <div className={`error-message ${isReconnecting ? 'warning' : ''}`} role="alert">
                {error}
                {error.includes('Maximum reconnection attempts') && (
                  <button onClick={handleManualReconnect} className="retry-button">
                    Retry Connection
                  </button>
                )}
              </div>
            )}

            {!isStreaming && !isConnecting ? (
              <AudioSourceSelector
                onSourceSelected={handleAudioSourceSelected}
                onError={handleAudioError}
              />
            ) : (
              <>
                {isConnecting && (
                  <div className="connecting-message">
                    <p>Connecting to streaming server...</p>
                  </div>
                )}

                {isStreaming && (
                  <>
                    <div className="active-stream-info">
                      <p><strong>Audio Source:</strong> {audioConfig?.type}</p>
                      {audioConfig?.microphoneDeviceId && (
                        <p><strong>Microphone:</strong> {audioConfig.microphoneDeviceId.slice(0, 8)}...</p>
                      )}
                      <p><strong>Connection:</strong> {connectionState}</p>
                    </div>

                    <AudioLevelMeter stream={audioStream} />

                    {audioConfig?.mixer && (
                      <AudioMixerControls
                        mixer={audioConfig.mixer}
                        onMixRatioChange={handleMixRatioChange}
                      />
                    )}

                    <button onClick={() => handleStopStreaming()} className="warning-button">
                      Stop Streaming
                    </button>
                  </>
                )}
              </>
            )}

            <div className="status">
              Status: {isReconnecting ? '🟡 Reconnecting' : isStreaming ? '🔴 Live' : isConnecting ? '🟡 Connecting' : '⚫ Offline'}
            </div>
          </div>

          <div className="session-actions">
            <button onClick={handleEndSession} className="danger-button">
              End Session
            </button>
          </div>
        </>
      )}
    </div>
  );
}
